import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {RootState, useAppDispatch} from "../../../redux/store";

// Import slice - PATH RELATIVI!
import {
    fetchOstacoli,
    Ostacolo,
    OstacoliList,
    TIPI_OSTACOLO,
    OSTACOLO_ICON_MAP,
    setOstacoloCorrenteId,
} from "./ostacoliSlice";

import {
    FiltriOstacoli,
    setFiltriOstacoli,
    resetFiltriOstacoli,
} from "components/userPreference/userPreferenceSlice";

import {ErrorStatusTypes} from "components/common/errorsDeclarations";

// Componente form
import OstacoloForm from "./OstacoloForm";

// Material UI
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Switch from "@mui/material/Switch";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import BlockIcon from "@mui/icons-material/Block";
import AddIcon from "@mui/icons-material/Add";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import {theme} from "../../theme";
import {convertToDateTime} from "../../common/dateUtils/convertToDateTime";

// ============================================
// COMPONENT
// ============================================

const OstacoliPage = () => {
    const dispatch = useAppDispatch();

    // ============================================
    // SELECTORS
    // ============================================

    const ostacoliList = useSelector<RootState, OstacoliList>(
        (state) => state.ostacoliState.ostacoliList
    );

    const filtri = useSelector<RootState, FiltriOstacoli>(
        (state) => state.userPreferenceState.filtriOstacoli || {}
    );

    const ostacoloCorrenteId = useSelector<RootState, number | undefined>(
        (state) => state.ostacoliState.ostacoloCorrenteId
    );

    // ============================================
    // STATI LOCALI
    // ============================================

    const [searchValue, setSearchValue] = useState(filtri?.search || "");
    const [refresh, setRefresh] = useState(false);
    const [tipiSelezionati, setTipiSelezionati] = useState<string[]>(
        filtri?.tipi || []
    );
    const [soloNonRisolti, setSoloNonRisolti] = useState<boolean>(
        filtri?.risolto === false
    );
    const [anchorElTipoFilter, setAnchorElTipoFilter] =
        useState<null | HTMLElement>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        const filtriIniziali: FiltriOstacoli = {
            ...filtri,
            numero_record: filtri.numero_record || 20,
            page: filtri.page || 1,
        };
        dispatch(setFiltriOstacoli(filtriIniziali));
        dispatch(fetchOstacoli(filtriIniziali));
    }, []);

    useEffect(() => {
        if (refresh) {
            const nuoviFiltri: FiltriOstacoli = {
                ...filtri,
                search: searchValue || undefined,
                tipi: tipiSelezionati.length > 0 ? tipiSelezionati : undefined,
                risolto: soloNonRisolti ? false : undefined,
                page: 1,
            };

            dispatch(setFiltriOstacoli(nuoviFiltri));
            dispatch(fetchOstacoli(nuoviFiltri));
            setRefresh(false);
        }
    }, [refresh]);

    // ============================================
    // HANDLERS
    // ============================================

    const handleResetFiltri = () => {
        setSearchValue("");
        setTipiSelezionati([]);
        setSoloNonRisolti(false);

        dispatch(resetFiltriOstacoli());

        const filtriDefault: FiltriOstacoli = {
            numero_record: 20,
            page: 1,
        };
        dispatch(setFiltriOstacoli(filtriDefault));
        dispatch(fetchOstacoli(filtriDefault));
    };

    const handlePaginationChange = (
        event: React.ChangeEvent<unknown>,
        page: number
    ) => {
        const nuoviFiltri = {...filtri, page};
        dispatch(setFiltriOstacoli(nuoviFiltri));
        dispatch(fetchOstacoli(nuoviFiltri));
    };

    const handleRecordsPerPageChange = (event: SelectChangeEvent<number>) => {
        const numero_record = event.target.value as number;
        const nuoviFiltri: FiltriOstacoli = {
            ...filtri,
            numero_record,
            page: 1,
        };
        dispatch(setFiltriOstacoli(nuoviFiltri));
        dispatch(fetchOstacoli(nuoviFiltri));
    };

    const toggleTipo = (tipo: string) => {
        setTipiSelezionati((prev) =>
            prev.includes(tipo)
                ? prev.filter((t) => t !== tipo)
                : [...prev, tipo]
        );
    };

    const handleNuovoOstacolo = () => {
        dispatch(setOstacoloCorrenteId(undefined));
        setDialogOpen(true);
    };

    const handleEditOstacolo = (ostacolo: Ostacolo) => {
        dispatch(setOstacoloCorrenteId(ostacolo.id));
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        dispatch(setOstacoloCorrenteId(undefined));
    };

    // ============================================
    // RENDER
    // ============================================

    return (
        <Box sx={{p: 3}}>
            {/* Header */}
            <Grid container spacing={2} sx={{mb: 1}}>
                <Grid size={{xs: 12, sm: 6}}>
                    <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                        <Typography variant="h5" fontWeight="bold">
                            Ostacoli
                        </Typography>
                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleNuovoOstacolo}
                        >
                            Nuovo Ostacolo
                        </Button>
                    </Stack>
                </Grid>

                <Grid size={{xs: 12, sm: 6}}>
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{justifyContent: "flex-end"}}
                    >
                        <FormControl
                            sx={{m: 1, minWidth: 250, width: "70%"}}
                            variant="outlined"
                            size="small"
                        >
                            <InputLabel>Ricerca</InputLabel>
                            <OutlinedInput
                                type="text"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setRefresh(true)}
                                            edge="end"
                                            size="small"
                                        >
                                            <SearchIcon />
                                        </IconButton>
                                    </InputAdornment>
                                }
                                label="Ricerca"
                                placeholder="Descrizione, articolo, cliente..."
                                value={searchValue}
                                onChange={(e) => setSearchValue(e.target.value)}
                                onBlur={() => setRefresh(true)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        setRefresh(true);
                                    }
                                }}
                            />
                        </FormControl>
                    </Stack>
                </Grid>
            </Grid>

            {/* Filtri */}
            <Card sx={{mb: theme.spacing(2), p: theme.spacing(2)}}>
                <Grid container spacing={2} sx={{alignItems: "center", flexWrap: "nowrap"}}>
                    {/* Filtro tipi */}
                    <Grid size="auto">
                        <Stack direction="row" spacing={1} sx={{alignItems: "center", flexWrap: "wrap", gap: 1}}>
                            <Typography variant="body2" fontWeight="bold">
                                Tipo:
                            </Typography>
                            {tipiSelezionati.length > 2 ? (
                                <Chip
                                    label={`${tipiSelezionati.length} tipi`}
                                    color="primary"
                                    size="small"
                                    onDelete={() => {
                                        setTipiSelezionati([]);
                                        setRefresh(true);
                                    }}
                                />
                            ) : tipiSelezionati.length > 0 ? (
                                tipiSelezionati.map((tipo) => (
                                    <Chip
                                        key={tipo}
                                        label={TIPI_OSTACOLO.find((t) => t.value === tipo)?.label || tipo}
                                        color="warning"
                                        size="small"
                                        onDelete={() => {
                                            toggleTipo(tipo);
                                            setRefresh(true);
                                        }}
                                    />
                                ))
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    Tutti
                                </Typography>
                            )}
                            <IconButton
                                size="small"
                                onClick={(e) => setAnchorElTipoFilter(e.currentTarget)}
                                sx={{
                                    color: tipiSelezionati.length > 0 ? "primary.main" : "inherit",
                                }}
                            >
                                <FilterAltIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Grid>

                    {/* Filtro solo non risolti */}
                    <Grid size="auto">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={soloNonRisolti}
                                    onChange={(e) => {
                                        setSoloNonRisolti(e.target.checked);
                                        setRefresh(true);
                                    }}
                                    size="small"
                                />
                            }
                            label={
                                <Typography variant="body2" fontWeight="bold">
                                    Solo non risolti
                                </Typography>
                            }
                            sx={{
                                backgroundColor: soloNonRisolti
                                    ? theme.palette.action.selected
                                    : "inherit",
                                borderRadius: 1,
                                px: 1,
                            }}
                        />
                    </Grid>

                    {/* Reset filtri */}
                    <Grid size="auto" sx={{ml: "auto"}}>
                        <IconButton onClick={handleResetFiltri} color="primary">
                            <FilterAltOffIcon />
                        </IconButton>
                    </Grid>
                </Grid>
            </Card>

            {/* Lista Ostacoli */}
            <Box>
                {ostacoliList.results.map((ostacolo: Ostacolo) => (
                    <Card
                        key={ostacolo.id}
                        sx={{
                            mb: theme.spacing(1),
                            cursor: "pointer",
                            border: ostacolo.is_scaduto
                                ? `2px solid ${theme.palette.error.main}`
                                : `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                                backgroundColor: theme.palette.action.hover,
                            },
                        }}
                        onClick={() => handleEditOstacolo(ostacolo)}
                    >
                        <CardContent sx={{py: 1.5, "&:last-child": {pb: 1.5}}}>
                            <Grid container spacing={2} sx={{alignItems: "center"}}>
                                <Grid size={{xs: 12, sm: 2}}>
                                    {(() => {
                                        const IconComp = OSTACOLO_ICON_MAP[ostacolo.tipo || "ALT"] || HelpOutlineIcon;
                                        return (
                                            <Chip
                                                icon={<IconComp sx={{fontSize: 16}} />}
                                                label={ostacolo.tipo_display}
                                                color="warning"
                                                size="small"
                                                variant={ostacolo.risolto ? "outlined" : "filled"}
                                            />
                                        );
                                    })()}
                                </Grid>
                                <Grid size={{xs: 12, sm: 3}}>
                                    <Typography variant="body2" fontWeight="bold">
                                        {ostacolo.odp_display}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {ostacolo.codice_articolo} - {ostacolo.nome_cliente}
                                    </Typography>
                                </Grid>
                                <Grid size={{xs: 12, sm: 3}}>
                                    <Typography variant="body2">
                                        {ostacolo.descrizione}
                                    </Typography>
                                </Grid>
                                <Grid size={{xs: 12, sm: 2}}>
                                    <Typography variant="caption" color="text.secondary">
                                        Segnalato: {convertToDateTime(ostacolo.data_segnalazione)?.toFormat("dd/MM/yyyy")}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption" color="text.secondary">
                                        Vincolo: {convertToDateTime(ostacolo.data_prevista_risoluzione)?.toFormat("dd/MM/yyyy")}
                                    </Typography>
                                </Grid>
                                <Grid size={{xs: 12, sm: 2}}>
                                    <Stack direction="row" spacing={1} sx={{alignItems: "center", justifyContent: "flex-end"}}>
                                        {ostacolo.risolto ? (
                                            <Chip label="Risolto" color="success" size="small" />
                                        ) : ostacolo.is_scaduto ? (
                                            <Chip
                                                icon={<WarningAmberIcon />}
                                                label={`Scaduto (${ostacolo.giorni_ritardo}gg)`}
                                                color="error"
                                                size="small"
                                            />
                                        ) : (
                                            <Chip label="Aperto" color="info" size="small" />
                                        )}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ))}

                {/* Empty state */}
                {ostacoliList.results.length === 0 &&
                    ostacoliList.errorsStack.status !== ErrorStatusTypes.PENDING && (
                        <Card>
                            <CardContent>
                                <Box sx={{textAlign: "center", py: 4}}>
                                    <BlockIcon
                                        sx={{fontSize: 64, color: "grey.400", mb: 2}}
                                    />
                                    <Typography variant="h6" color="text.secondary">
                                        Nessun ostacolo trovato
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                {/* Paginazione */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 3,
                    }}
                >
                    <FormControl sx={{minWidth: 120}} size="small">
                        <InputLabel>Record per pagina</InputLabel>
                        <Select
                            value={filtri.numero_record || 20}
                            label="Record per pagina"
                            onChange={handleRecordsPerPageChange}
                        >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                        </Select>
                    </FormControl>

                    <Pagination
                        count={ostacoliList.num_pages}
                        page={filtri.page || 1}
                        onChange={handlePaginationChange}
                        color="primary"
                    />

                    <Typography variant="body2" color="text.secondary">
                        Totale: {ostacoliList.count} record
                    </Typography>
                </Box>
            </Box>

            {/* Menu filtro tipi */}
            <Menu
                anchorEl={anchorElTipoFilter}
                open={Boolean(anchorElTipoFilter)}
                onClose={() => setAnchorElTipoFilter(null)}
            >
                <FormGroup sx={{px: 2}}>
                    {TIPI_OSTACOLO.map((tipo) => (
                        <FormControlLabel
                            key={tipo.value}
                            control={
                                <Checkbox
                                    checked={tipiSelezionati.includes(tipo.value)}
                                    onChange={() => toggleTipo(tipo.value)}
                                />
                            }
                            label={tipo.label}
                        />
                    ))}
                </FormGroup>
                <Box sx={{px: 2, pb: 1}}>
                    <Button
                        onClick={() => {
                            setAnchorElTipoFilter(null);
                            setRefresh(true);
                        }}
                        variant="contained"
                        size="small"
                        fullWidth
                    >
                        Applica
                    </Button>
                </Box>
            </Menu>

            {/* Dialog editing ostacolo */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {ostacoloCorrenteId ? "Modifica Ostacolo" : "Nuovo Ostacolo"}
                </DialogTitle>
                <DialogContent>
                    <OstacoloForm
                        ostacoloId={ostacoloCorrenteId}
                        onClose={handleDialogClose}
                        onSaved={() => setRefresh(true)}
                    />
                </DialogContent>
            </Dialog>

            {/* Loading */}
            <Backdrop
                open={ostacoliList.errorsStack.status === ErrorStatusTypes.PENDING}
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
};

export default OstacoliPage;
